import React, { useEffect } from "react"

import { HUBSPOT_SCRIPT_URL } from "~config/constants"
import COPY from "~config/copy-constants"
import TEST_ID_CONSTANTS from "~config/test-id-constants"

type HubspotFormProps = {
    readonly formId: string
    readonly region?: string
}

export default function HubspotForm({
    formId,
    region = "na1",
}: HubspotFormProps) {
    useHubspotForm({ formId, region })

    return (
        <div
            id={formId}
            data-testid={TEST_ID_CONSTANTS.HUBSPOT_FORM_CONTAINER}
        />
    )
}

function useHubspotForm({ formId, region = "na1" }: HubspotFormProps) {
    const MAX_RETRIES = 10
    const RETRY_INTERVAL = 100 // ms

    const loadScript = async (): Promise<void> => {
        if (window.hbspt?.forms) return

        const existing = document.querySelector(
            `script[src="${HUBSPOT_SCRIPT_URL}"]`
        )
        if (existing) {
            await new Promise<void>((resolve, reject) => {
                existing.addEventListener("load", () => resolve())
                existing.addEventListener("error", () =>
                    reject(new Error(COPY.HUBSPOT.SCRIPT_LOAD_ERROR))
                )
            })
            return
        }

        await new Promise<void>((resolve, reject) => {
            const script = document.createElement("script")
            script.src = HUBSPOT_SCRIPT_URL
            script.async = true
            script.onload = () => resolve()
            script.onerror = () =>
                reject(new Error(COPY.HUBSPOT.SCRIPT_LOAD_ERROR))
            document.body.appendChild(script)
        })
    }

    const waitForElement = async (id: string): Promise<HTMLElement> => {
        const checkElement = (attempt: number): Promise<HTMLElement> => {
            const el = document.getElementById(id)
            if (el) return Promise.resolve(el)
            if (attempt >= MAX_RETRIES) {
                throw new Error(
                    `Target element #${id} not found after ${MAX_RETRIES} attempts`
                )
            }
            return new Promise((resolve) =>
                setTimeout(
                    () => resolve(checkElement(attempt + 1)),
                    RETRY_INTERVAL
                )
            )
        }
        return checkElement(0)
    }

    const createHubspotForm = async () => {
        if (!window.hbspt?.forms) {
            throw new Error(COPY.HUBSPOT.API_NOT_AVAILABLE)
        }

        const portalId = process.env.GATSBY_HUBSPOT_ID
        if (!portalId) {
            throw new Error(COPY.HUBSPOT.PORTAL_ID_NOT_SET)
        }

        await waitForElement(formId)

        window.hbspt.forms.create({
            region,
            portalId,
            formId,
            target: `#${formId}`,
        })
    }

    useEffect(() => {
        const init = async () => {
            try {
                await loadScript()
                await createHubspotForm()
            } catch (err) {
                console.error(COPY.HUBSPOT.FORM_LOAD_ERROR, err)
            }
        }

        void init()
    }, [formId, region])
}
